/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */

import {Component, Prop, Vue} from 'vue-property-decorator';

import {
    UgcBuild,
    UgcBuildInterface,
    UgcBuildPresignedResponseInterface,
    UgcRevisionInterface,
} from '@/store/modules/ugc-build/types';
import {Action} from 'vuex-class';
import Loading from '@/components/Loading/index.vue';
import {LoadingState, LoadingStateInterface} from '@/components/Loading/types';
import FormTranslation from '@/mixins/FormTranslation';
import FlexiColourStrip from '@/components/FlexiColourStrip/index.vue';
import {mixins} from 'vue-class-component';
import JsonCategory from './data/category.json';
import JsonScenery from './data/scenery.json';
import JsonSoundSize from './data/sound.size.json';
import JsonSoundType from './data/sound.type.json';
import JsonTheme from './data/theme.json';
import UuidStatic from 'uuid';
import BuildThumbnail from '@/components/BuildThumbnail/scripts';
import {UgcItemInterface} from '@/store/modules/ugc-item/types';
import FileDragDrop from '@/components/FileDragDrop/index.vue';
import ApiException from '@/exceptions/ApiException';
import Tooltip from '@/components/Tooltip/index.vue';

const namespace: string = 'ugcBuildModule';

/**
 * Item create form component.
 *
 * This represents a list of user items that have been added to the system.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
@Component({
    name: 'c-item-create-form',
    components: {
        'c-loading': Loading,
        'c-build-thumbnail': BuildThumbnail,
        'c-flexi-colour-strip': FlexiColourStrip,
        'c-file-drag-drop': FileDragDrop,
        'c-tooltip': Tooltip,
    },
})
export default class ItemCreateForm extends mixins(FormTranslation) {
    @Action('createRevision', {namespace}) public createRevision: any;
    @Action('presigned', {namespace}) public presigned: any;
    @Action('presignedImage', {namespace}) public presignedImage: any;
    @Action('uploadBuildArtifact', {namespace}) public uploadBuildArtifact: any;
    @Prop(String) public itemId!: string;

    public item!: UgcItemInterface;

    public build: UgcBuildInterface = new UgcBuild();
    public zip: File|null = null;
    public jpg: File|null = null;

    public state: LoadingStateInterface = new LoadingState(false, false);
    public colors: string[] = ['#C0382B', '#8E43AD', '#F39C19', '#2980B9', '#27AF60', '#3D556E', '#BDC3C8', ''];

    public readonly MAX_FILE_SIZE_THUMBNAIL: number = 150000;
    public readonly MAX_FILE_SIZE_ZIP: number = 30000000;
    public readonly REGEX_ASCII: RegExp = /^[a-z0-9 _\-+.@#\[\]{}()*&^%$£!;:~<>,?=\r\n]+$/i;

    /**
     * Sound type data getter.
     *
     * @returns {string[]}
     */
    get soundType(): string[] {
        return JsonSoundType;
    }

    /**
     * Sound size data getter.
     *
     * @returns {string[]}
     */
    get soundSize(): string[] {
        return JsonSoundSize;
    }

    /**
     * Theme data getter.
     *
     * @returns {string[]}
     */
    get themes(): string[] {
        return JsonTheme;
    }

    /**
     * Scenery data getter.
     *
     * @returns {string[]}
     */
    get sceneries(): string[] {
        return JsonScenery;
    }

    /**
     * Category data getter.
     *
     * @returns {{[p: string]: string[]}}
     */
    get dataCategories(): {[s: string]: string[]} {
        return JsonCategory;
    }

    /**
     * Accessory for category groups.
     *
     * @returns {string[]}
     */
    get categoryGroups(): string[] {
        return Object.keys(this.dataCategories);
    }

    /**
     * Check if in revision mode.
     *
     * @returns {boolean}
     */
    get isRevision(): boolean {
        return undefined !== this.build.itemId;
    }

    /**
     * Item image checker.
     *
     * @returns {boolean}
     */
    get hasItemImageUuid(): boolean {
        return !!this.item && !!this.item.imgUuid && this.item.imgUuid !== '';
    }

    /**
     * Get the items for a category group.
     *
     * @param {string} group
     *
     * @returns {string[]}
     */
    public categoryItems(group: string): string[] {
        return this.dataCategories[group];
    }

    /**
     * Prevent illegal character keypresses for monetary fields.
     *
     * @param {KeyboardEvent} event
     */
    public moneyMask(event: KeyboardEvent): void {
        if (event.key.match(/^[.\d]$/)) {
            return;
        }

        switch (event.key.toLowerCase()) {
            case 'home':
            case 'end':
            case 'delete':
            case 'backspace':
            case 'arrowleft':
            case 'arrowright':
            case 'tab':
                return;
        }

        event.preventDefault();
    }

    /**
     * File change event.
     *
     * @param {string} fieldName
     * @param {FileList | File[]} fileList
     */
    public filesChange(fieldName: string, fileList: FileList|File[]): void {
        if ('jpg' !== fieldName && 'zip' !== fieldName) {
            return;
        }

        // need to remove file and re-validate
        if (1 !== fileList.length) {
            if ('zip' === fieldName) {
                this.zip = null;
            }

            if ('jpg' === fieldName) {
                this.jpg = null;
            }

            return;
        }

        // add files to relevant item
        for (const item of fileList) {
            if ('zip' === fieldName) {
                this.zip = item;
            }

            if ('jpg' === fieldName) {
                this.jpg = item;
            }
        }
    }

    /**
     * Refresh file status.
     *
     * @param {string} type
     *
     * @returns {Promise<void>}
     */
    public async refreshFile(type: string): Promise<void> {
        const file: File | null = ('zip' === type) ? this.zip : this.jpg;

        if (file instanceof File && !file.size) {
            const ref: any = this.$refs[`${type}Files`];
            if (!!ref.empty) {
                ref.empty();
            }
        }

        return this.$nextTick();
    }

    /**
     * Validation of form.
     *
     * @returns {Promise<boolean>}
     */
    public async validate(): Promise<boolean> {
        try {
            // tidy up data
            this.build.description = this.build.description.trim();
            this.build.name = this.build.name.trim();

            // refresh files to check for out of browser update
            await this.refreshFile('jpg');
            await this.refreshFile('zip');

            this.$validator.errors.clear();
            // const rules:
            const result: object|boolean = await this.$validator.validateAll();

            return !!result;
        } catch (e) {
            return false;
        }
    }

    /**
     * Submit the form.
     *
     * @returns {Promise<void>}
     */
    public async submit(): Promise<void> {
        try {
            if (!await this.validate()) {
                this.state.failed();
                return;
            }

            this.state.start();

            // If we have an item, and the item already has a uuid and the user wants to use the last image, use it.
            if (this.item && this.item.imgUuid && this.build.useLastImage) {
                this.build.imgUuid = this.item.imgUuid;
            }

            // If there is an item, and the user hasn't selected use last image, and has got a file to upload, use that.
            if (!this.build.useLastImage && this.jpg) {
                const ext: string = (this.jpg as File).name.split('.').pop() as string;
                this.build.imgUuid = `${UuidStatic.v4()}.${ext.toLowerCase()}`;
            }

            // create a revision
            const build: UgcRevisionInterface = await this.createRevision(this.build);
            const zipFile: File = this.zip as File;

            // upload the zip file
            await this.uploadFile(build, 'POST', zipFile.size, 'zip', zipFile);

            if (this.jpg && !this.build.useLastImage) {
                const imgFile: File = this.jpg as File;
                // upload the preview image
                await this.uploadImageFile(build, 'POST', imgFile.size, 'jpg', imgFile, this.build.imgUuid);
            }

            // redirect to the item show page
            this.$router.push({name: 'item-show', params: {itemId: build.itemId || ''}});

            // notify of success
            this.$toastr.success(this.$i18n.translate('notification.item-add.complete', {}) || '');
        } catch (e) {
            Vue.nextTick(() => {
                this.state.failed();
                if (e instanceof ApiException && 422 === e.status) {
                    this.$toastr.error(this.isRevision
                        ? this.$i18n.translate('error.builds.too-many', {}) || ''
                        : this.$i18n.translate('error.items.too-many', {}) || '');
                    return;
                }

                this.$toastr.error(this.$i18n.translate('notification.item-add.failed', {}) || '');
            });
        }
    }

    /**
     * Cancel the form.
     */
    public cancel(): void {
        this.$router.push({name: 'item'});
    }

    /**
     * Upload file using pre-signed url.
     *
     * @param {UgcRevisionInterface} build
     * @param {string} method
     * @param {number} size
     * @param {string} extension
     * @param {File} file
     *
     * @returns {Promise<void>}
     */
    private async uploadFile(
        build: UgcRevisionInterface,
        method: string,
        size: number,
        extension: string,
        file: File,
    ): Promise<void> {
        const url: UgcBuildPresignedResponseInterface = await this.presigned({build, method, size, extension});

        if (undefined === url) {
            throw new Error('File could not be found');
        }

        return await this.uploadBuildArtifact({url, file});
    }

    /**
     * Upload file using pre-signed url.
     *
     * @param {UgcRevisionInterface} build
     * @param {string} method
     * @param {number} size
     * @param {string} extension
     * @param {File} file
     * @param {string} filename
     *
     * @returns {Promise<void>}
     */
    private async uploadImageFile(
        build: UgcRevisionInterface,
        method: string,
        size: number,
        extension: string,
        file: File,
        filename?: string,
    ): Promise<void> {
        const params: object = {build, method, size, extension, filename};
        const url: UgcBuildPresignedResponseInterface = await this.presignedImage(params);

        if (undefined === url) {
            throw new Error('File could not be found');
        }

        return await this.uploadBuildArtifact({url, file});
    }
}
